import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Modal from 'antd/lib/modal'
import AcknowledgeForm from './AcknowledgeForm'
import TenantEstimatedEmployees from './EstimatedEmployees'
import { getLatestAcknowledgements } from '../Reports/Acknowledgement/Utils'
import Overlay from '../../Overlay'
import { useHistory, useLocation } from 'react-router-dom'

// The Estimated Population step is still owed until a positive number is on
// file. A brand-new tenant's TenantPopulation record comes back with
// estimated null/blank (not 0), and the value is saved as a string — a strict
// `=== 0` check missed both, so the last first-login popup never appeared
// (and the post-acknowledgement refresh closed it again if it had).
const populationNeeded = (population) => !(Number(population?.estimated) > 0)

// hideForTeams: the team Accept/Decline window is pending or on screen. The
// first-login order is Confirm User Information -> Terms of Use -> team
// Accept/Decline -> Tenant Activation Policy -> Estimated Population, so
// this whole configurator waits behind the three earlier windows.
const TenantConfigurator = ({ acknowledgement, population, loading, onRefresh, hideForTerms, hideForUserConfig, hideForTeams = false, onVisibilityChange }) => {
    const [showAcknowledgement, setShowAcknowledgement] = useState(false)
    const [showPopulation, setShowPopulation] = useState(false)
    const user = useSelector((state) => state.app.user)
    const building = useSelector((state) => state.app.building)
    const tenant = useSelector((state) => state.app.tenant)
    const history = useHistory()
    const location = useLocation()

    // Check if we're on a page where the tenant configurator should be shown
    // The tenant configurator should only appear on the dashboard, root page, training portal, or training program pages
    const isOnDashboardOrRoot = location.pathname === '/' ||
        location.pathname === '/dashboard' ||
        location.pathname === '/trainingportal' ||
        location.pathname.startsWith('/trainingprogram')

    // Wait behind every earlier first-login window (Confirm User Information,
    // Terms of Use, team Accept/Decline) so the popups never overlap and the
    // order holds. The acknowledgement and population steps still sequence
    // internally: population follows acknowledgement immediately.
    const blockedByEarlierWindow = hideForTerms || hideForUserConfig || hideForTeams
    const shouldBeVisible = isOnDashboardOrRoot
        && !blockedByEarlierWindow
        && (showAcknowledgement || showPopulation)

    // Notify parent when visibility changes
    useEffect(() => {
        if (onVisibilityChange) {
            onVisibilityChange(shouldBeVisible);
        }
    }, [shouldBeVisible, onVisibilityChange]);

    useEffect(() => {
        if (!loading) {
            // Check if user is a tenant admin
            const isTenantAdmin = user.roles?.includes('TENANT_ADMIN')

            // For tenant admins, the activation policy should show regardless of other setup completion
            // For non-tenant admins, don't show anything (they shouldn't see this modal at all)
            if (!isTenantAdmin) {
                setShowAcknowledgement(false)
                setShowPopulation(false)
                return
            }

            // For tenant admins, only check basic user existence (not full configuration completion)
            // This prevents the modal from showing during the very initial login process
            // but allows it to show once the user object is populated
            if (!user.pk || !user.firstName || !user.lastName) {
                setShowAcknowledgement(false)
                setShowPopulation(false)
                return
            }

            // Remove Terms of Use coordination logic - let z-index handle layering
            // This prevents issues with Redux state updates and timing

            const latestAck = getLatestAcknowledgements(acknowledgement)

            // Determine if we should show the acknowledgment form
            let shouldShowAcknowledgement = false

            if (!latestAck) {
                // No acknowledgments exist, show the form
                shouldShowAcknowledgement = true
            } else if (latestAck.declined) {
                // Latest acknowledgment was declined, show the form
                shouldShowAcknowledgement = true
            } else if (!latestAck.active) {
                // Latest acknowledgment is not active, show the form
                shouldShowAcknowledgement = true
            } else {
                // Latest acknowledgment is active (accepted)

                // If the latest acknowledgment was accepted by this user, don't show the form
                if (latestAck.acknowledgedBy?.pk === user.pk) {
                    shouldShowAcknowledgement = false
                } else {
                    // Latest acknowledgment was accepted by someone else
                    // Check if this user has declined before
                    if (acknowledgement && acknowledgement.acknowledgements) {
                        const userDeclinedAcks = acknowledgement.acknowledgements.filter(ack =>
                            ack.acknowledgedBy?.pk === user.pk && ack.declined
                        )

                        if (userDeclinedAcks.length > 0) {
                            // This user has declined before, check if someone else accepted after the decline
                            const userLatestDecline = userDeclinedAcks.reduce((latest, current) =>
                                (!latest.timestamp || current.timestamp > latest.timestamp) ? current : latest
                            )

                            // If the latest overall acknowledgment is accepted and happened after this user's decline
                            if (latestAck.active && !latestAck.declined && latestAck.timestamp > userLatestDecline.timestamp) {
                                // Another admin accepted after this user declined, don't show the form
                                shouldShowAcknowledgement = false
                            } else {
                                // This user declined and no one else has accepted since, show the form
                                shouldShowAcknowledgement = true
                            }
                        } else {
                            // This user hasn't declined, but someone else has accepted
                            // Don't show the form since the policy is already accepted
                            shouldShowAcknowledgement = false
                        }
                    } else {
                        // No acknowledgment data, but latestAck exists - should not happen, but be safe
                        shouldShowAcknowledgement = false
                    }
                }
            }

            setShowAcknowledgement(shouldShowAcknowledgement)

            // Show the population form once the acknowledgement is out of the
            // way and no positive estimate is on file yet.
            const shouldShowPopulation = populationNeeded(population) && !shouldShowAcknowledgement

            if (shouldShowPopulation) {
                setShowPopulation(true)
            } else {
                setShowPopulation(false)
            }
        }
    }, [acknowledgement, population, loading, user.pk, user.firstName, user.lastName, user.roles, building])

    const onAcknowledged = () => {
        setShowAcknowledgement(false)

        // Always check population after acknowledgement — this is the step
        // that follows the Tenant Activation Policy in the first-login order.
        if (populationNeeded(population)) {
            setShowPopulation(true)
        } else {
            // FIXED: Only navigate to admin dashboard if we're on the dashboard or root page
            // This prevents navigation when the tenant configurator appears on other pages
            if (isOnDashboardOrRoot) {
                setTimeout(() => {
                    history.push(`/admin/tenants/${tenant.pk}`)
                }, 500)
            }
        }
    }

    const onEstimatedPopulationUpdated = (item) => {
        if (item.data) {
            setShowPopulation(false)

            // FIXED: Only navigate to admin dashboard if we're on the dashboard or root page
            // This prevents navigation when the tenant configurator appears on other pages
            if (isOnDashboardOrRoot) {
                setTimeout(() => {
                    history.push(`/admin/tenants/${tenant.pk}`)
                }, 500)
            }
        }
    }

    return (
        <Overlay
            onClose={() => Modal.error({ title: 'Please complete the action to proceed.' })}
            visible={shouldBeVisible}
            headerText={acknowledgement ? 'BSS Tenant Activation Policy' : 'Tenant Setup'}
            closebutton={false}
            zIndex={1000}
        >
            <div style={{ padding: '1.64em', width: 505 }}>
                {showAcknowledgement && <AcknowledgeForm callback={onAcknowledged} onRefresh={onRefresh} />}
                {showPopulation && !showAcknowledgement && <TenantEstimatedEmployees onUpdate={onEstimatedPopulationUpdated} />}
            </div>
        </Overlay>
    )
}

export default TenantConfigurator
